import { useCallback, useEffect, useRef, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { Counter, Zoom } from "yet-another-react-lightbox/plugins";
import "yet-another-react-lightbox/plugins/counter.css";
import type { GalleryImage } from "@/types";

const galleryImages: readonly GalleryImage[] = [
	{
		src: "/gallery/1.jpeg",
		width: 800,
		height: 400,
		title: "Fahrzeugaufbereitung",
	},
	{ src: "/gallery/2.jpeg", width: 800, height: 600, title: "Lackpolitur" },
	{
		src: "/gallery/3.jpeg",
		width: 800,
		height: 600,
		title: "Innenraumreinigung",
	},
	{ src: "/gallery/4.jpeg", width: 600, height: 600, title: "Felgenreinigung" },
	{ src: "/gallery/5.jpeg", width: 800, height: 600, title: "Versiegelung" },
	{ src: "/gallery/6.jpeg", width: 600, height: 600, title: "Details" },
	{
		src: "/gallery/7.jpeg",
		width: 600,
		height: 600,
		title: "Professionelle Arbeit",
	},
	{ src: "/gallery/8.jpeg", width: 600, height: 600, title: "Fahrzeugpflege" },
	{ src: "/gallery/9.jpeg", width: 600, height: 800, title: "Innenraum" },
	{ src: "/gallery/10.jpeg", width: 600, height: 600, title: "Motorwäsche" },
	{ src: "/gallery/11.jpeg", width: 600, height: 600, title: "Lederpflege" },
	{
		src: "/gallery/12.jpeg",
		width: 600,
		height: 600,
		title: "Polsterreinigung",
	},
	{
		src: "/gallery/13.jpeg",
		width: 800,
		height: 600,
		title: "Keramikversiegelung",
	},
	{
		src: "/gallery/14.jpeg",
		width: 600,
		height: 600,
		title: "Wachsversiegelung",
	},
	{
		src: "/gallery/15.jpeg",
		width: 600,
		height: 600,
		title: "Nanoversiegelung",
	},
	{
		src: "/gallery/16.jpeg",
		width: 600,
		height: 600,
		title: "Premium Service",
	},
	{
		src: "/gallery/17.jpeg",
		width: 600,
		height: 800,
		title: "Qualitätsarbeit",
	},
	{ src: "/gallery/18.jpeg", width: 600, height: 600, title: "Expertise" },
	{
		src: "/gallery/19.jpeg",
		width: 600,
		height: 600,
		title: "Fahrzeugaufbereitung",
	},
	{ src: "/gallery/20.jpeg", width: 600, height: 600, title: "Autopflege" },
	{ src: "/gallery/21.jpeg", width: 800, height: 600, title: "Fahrzeugpflege" },
	{ src: "/gallery/22.jpeg", width: 600, height: 600, title: "Detailarbeit" },
	{ src: "/gallery/23.jpeg", width: 600, height: 600, title: "Professionell" },
	{ src: "/gallery/24.jpeg", width: 600, height: 600, title: "Ergebnis" },
] as const;

const Gallery: React.FC = () => {
	const [open, setOpen] = useState<boolean>(false);
	const [index, setIndex] = useState<number>(0);
	const [visibleImages, setVisibleImages] = useState<Set<number>>(new Set());
	const sectionRef = useRef<HTMLElement | null>(null);
	const imageRefs = useRef<(HTMLDivElement | null)[]>([]);

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const imgIndex = parseInt(
						entry.target.getAttribute("data-index") ?? "0",
						10,
					);
					if (entry.isIntersecting) {
						setVisibleImages((prev) => new Set([...prev, imgIndex]));
					}
				});
			},
			{ threshold: 0.1, rootMargin: "50px" },
		);

		imageRefs.current.forEach((img) => {
			if (img) observer.observe(img);
		});

		return () => observer.disconnect();
	}, []);

	const handleImageClick = useCallback((idx: number): void => {
		setIndex(idx);
		setOpen(true);
	}, []);

	const getGridClass = useCallback(
		(width: number, height: number, idx: number): string => {
			const ratio = width / height;
			if (idx < 2) return "md:col-span-2 md:row-span-2";
			if (ratio > 1.5) return "md:col-span-2";
			if (ratio < 0.8) return "md:row-span-2";
			return "";
		},
		[],
	);

	return (
		<section id="works" ref={sectionRef} className="bg-black text-white py-20">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Section Header */}
				<div className="text-center mb-12">
					<h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 uppercase">
						Ein kleiner Auszug unserer Arbeit
					</h2>
					<p className="text-lg text-white/70">
						Überzeuge Dich selbst von unserer Arbeit.
					</p>
				</div>

				{/* Masonry Grid Gallery */}
				<div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px]">
					{galleryImages.map((image, idx) => (
						<div
							key={idx}
							ref={(el) => {
								imageRefs.current[idx] = el;
							}}
							data-index={idx}
							className={`relative overflow-hidden rounded-lg cursor-pointer group ${getGridClass(
								image.width,
								image.height,
								idx,
							)}`}
							onClick={() => handleImageClick(idx)}
						>
							<img
								src={image.src}
								alt={image.title}
								loading="lazy"
								className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-110 ${
									visibleImages.has(idx) ? "opacity-100" : "opacity-0"
								}`}
							/>
							{/* Overlay */}
							<div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
								<div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
									<ZoomIcon />
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Lightbox */}
			<Lightbox
				open={open}
				close={() => setOpen(false)}
				index={index}
				slides={galleryImages.map((img) => ({
					src: img.src,
					title: img.title,
					description: `${galleryImages.indexOf(img) + 1} / ${galleryImages.length}`,
				}))}
				plugins={[Zoom, Counter]}
				animation={{ fade: 300 }}
				carousel={{ finite: false }}
				zoom={{ maxZoomPixelRatio: 3 }}
				counter={{ container: { style: { top: 0 } } }}
			/>
		</section>
	);
};

const ZoomIcon: React.FC = () => (
	<svg
		className="w-10 h-10 text-white"
		fill="none"
		stroke="currentColor"
		viewBox="0 0 24 24"
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
		/>
	</svg>
);

export default Gallery;
